import { defineComponent as _defineComponent } from 'vue'
import { computed, watch, ref, Ref } from 'vue'
import { v4 as uuidv4 } from 'uuid'

import RichTextEditor from '@/components/core/RichTextEditor.vue'
import LinkedDayItem from '@/components/trip/travelPlan/waypoint/LinkedDayItem.vue'
import LinkedDayDatePickerModal, {
  NewLinkedDay,
} from '@/components/trip/travelPlan/waypoint/LinkedDayDatePickerModal.vue'
import LocationSearch from '@/components/core/LocationSearch.vue'
import LButton from '@/components/core/Button.vue'
import DeleteModal from '@/components/core/DeleteModal.vue'
import DropdownMenu from '@/components/core/DropdownMenu.vue'
import DropdownMenuItem from '@/components/core/DropdownMenuItem.vue'
import PictureViewer from '@/components/PictureViewer.vue'
import { useContext } from '@/composables/context'
import { useDailyPlan } from '@/composables/dailyPlan'
import {
  useCreateWaypointDayItem,
  useCreateActivity,
  useUpdateActivity,
  useDeleteActivity,
  useRearrangeWaypointDayItem,
  useUpdateWaypointDayItem,
  useDeleteWaypointDayItem,
} from '@/composables/waypoint'
import { useSelectedTrip } from '@/composables/trip'
import { eventModule, tripModule } from '@/store'
import { locationToAutocompleteResult } from '@/utils/location'
import { WaypointDayItemType, supportedPhotoFileTypes } from '@/utils/enums'
import { openInGoogleMaps, thumbnailImage } from '@/utils/utility-manager'
import {
  LocationAutocompleteResult,
  WaypointDay,
  LinkedDay,
  RearrangeWaypointDayItem,
  Waypoint,
} from '@/types/trip'
import { Activity, ActivityCreate, ActivityUpdate } from '@/types/activity'
import { TimeRange } from '@/types/types'
import AWSManager from '@/utils/aws-manager'

interface ActivityDialogProps {
  value?: boolean
  activity?: Partial<Activity>
  waypointDay?: WaypointDay
  waypoint: Waypoint
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ActivityDialog',
  props: {
    value: { type: Boolean, default: undefined },
    activity: { default: () => ({}) },
    waypointDay: { default: undefined },
    waypoint: null
  },
  emits: ['saved', 'input'],
  setup(__props: any, { emit: emits }) {

const props = __props as { value: boolean, activity: Partial<Activity>, waypointDay: WaypointDay, waypoint: Waypoint };

const { getEmojiFromText, getSearchUnsplashPhotos } = useDailyPlan()

const { i18n } = useContext()

// Props and emits -----------------------------------------





// Reactivity setup ----------------------------------------

const trip = useSelectedTrip()

const locationSearch = ref<InstanceType<typeof LocationSearch> | null>(null)

const showDialog = ref(false)
const isOpen = computed({
  get: () => props.value ?? showDialog.value,
  set: (value: boolean) => {
    showDialog.value = value
    emits('input', value)
  },
})

const isNew = !props.activity._id
const image = ref<string | undefined>(undefined)
const category = ref<string>()
const name = ref<string>('')
// used to check later if the name changed after focusing out of the input field
let previousName = name.value
const location = ref<LocationAutocompleteResult>()
const summary = ref<string>('')
const linkedDays: Ref<LinkedDay[]> = computed(() => {
  return props.activity.days ?? []
})
const selectedLinkedDay = ref<LinkedDay>()
const showDatePicker = ref(false)

const showLightBox = ref(false)
const activityImgs = computed(() => {
  return props.activity.images ?? []
})

const activityPhotoUploader = ref<HTMLInputElement | null>(null)
const filePath = ref('')
const emojis = {
  general: '⭐️',
  hiking: '🥾',
  camping: '🏕️',
  canoe_tour: '🛶',
  bike_tour: '🚲',
  beach: '🏖️',
  swimming: '🏊‍♂️',
  surfing: '🏄🌊',
  snorkeling: '🤿',
  boat_tour: '🛥️',
  campfire: '🔥',
  picknick: '🧺',
  party: '🎉',
  drinks: '🍻',
  food: '🍽️',
  horse_riding: '🐎',
  sport_event: '🏟️',
  concert: '🎤🎺',
  cinema: '🍿',
  winter_sports: '❄️',
}

// only disable save button when an activity is new and the name is not set.
// better UX for now because of the process when changing linked day or time.
const isValidChange = computed(() => {
  if (isNew) {
    // only the name must be set for a new activity
    return (
      name.value.trim().length !== 0 &&
      name.value !== i18n.t('enterNamePlaceholder')
    )
  }
  return true
})

// Mutations -----------------------------------------------

watch(
  isOpen,
  () => {
    if (isOpen.value) {
      initializeValues()
    }
  },
  { immediate: true },
)

const createActivityMutation = useCreateActivity(
  trip.value.id,
  props.waypoint._id,
)
const updateActivityMutation = useUpdateActivity(
  trip.value.id,
  props.waypoint._id,
)
const deleteActivityMutation = useDeleteActivity(trip.value.id)

const linkActivityToDayMutation = useCreateWaypointDayItem(trip.value.id)
const updateWaypointDayItemMutation = useUpdateWaypointDayItem(
  trip.value.id,
  props.waypoint._id,
)
const unlinkActivityFromDayMutation = useDeleteWaypointDayItem(
  trip.value.id,
  props.waypoint._id,
)
const rearrangeWaypointDayItemMutation = useRearrangeWaypointDayItem(
  trip.value.id,
)

const isLoading: Ref<false | true> =
  createActivityMutation.isLoading ||
  updateActivityMutation.isLoading ||
  deleteActivityMutation.isLoading ||
  linkActivityToDayMutation.isLoading ||
  updateWaypointDayItemMutation.isLoading ||
  unlinkActivityFromDayMutation.isLoading ||
  rearrangeWaypointDayItemMutation.isLoading

// Functions -----------------------------------------------

function initializeValues() {
  image.value =
    props.activity.images && props.activity.images.length > 0
      ? props.activity.images[0]
      : undefined
  category.value = props.activity.category
  name.value = props.activity.name ?? ''
  previousName = name.value
  location.value = props.activity.location?.dataSource
    ? locationToAutocompleteResult(props.activity.location)
    : undefined
  summary.value = props.activity.summary ?? ''

  if (activityPhotoUploader.value) {
    activityPhotoUploader.value.value = ''
  }
  // check if the activity dialog is opend from a waypoint day context
  if (
    !props.activity._id &&
    props.waypointDay &&
    linkedDays.value.length === 0
  ) {
    // new activity will be created with a specified waypoint day,
    // so allready add the waypoint day as a linked day first.
    linkedDays.value.push({
      date: props.waypointDay.date,
      dayId: props.waypointDay._id,
      itemId: '',
    })
  }
}

async function onNameFieldFocusout() {
  if (previousName === name.value || name.value.length < 2) {
    // current name has no change
    return
  }
  previousName = name.value

  category.value = await getEmojiFromText(name.value)

  image.value = await getSearchUnsplashPhotos(name.value)
}

function setCategoryEmoji(emoji: string) {
  category.value = emoji
}

function closeDialog() {
  isOpen.value = false
}

function deleteActivity() {
  if (!props.activity._id) {
    return
  }

  deleteActivityMutation.mutate(
    {
      waypointId: props.waypoint._id,
      activityId: props.activity._id,
    },
    { onSuccess: () => tripModule.fetch(trip.value.id) },
  )
  closeDialog()
}

async function saveActivity() {
  if (isNew) {
    await createActivity()
  } else {
    await updateActivity()
  }
}

async function createActivity() {
  if (!isNew) {
    return
  }

  if (filePath.value) {
    image.value = filePath.value
  }

  const newActivity: ActivityCreate = {
    name: name.value,
    images: image.value ? [image.value] : [],
  }

  if (category.value) {
    newActivity.category = category.value
  }
  if (location.value) {
    newActivity.location = await locationSearch.value?.fetchDetails()
  }
  if (summary.value !== '') {
    newActivity.summary = summary.value
  }

  const createdActivity = await createActivityMutation.mutateAsync(newActivity)

  // link the new activity to the added waypoint days
  if (linkedDays.value.length > 0) {
    for (const linkedDay of linkedDays.value) {
      await linkActivityToDayMutation.mutateAsync({
        waypointId: props.waypoint._id,
        waypointDayId: linkedDay.dayId,
        waypointDayDate: linkedDay.date,
        newItem: {
          activity: createdActivity,
          type: WaypointDayItemType.ACTIVITY,
          startTime: linkedDay.startTime ?? undefined,
          endTime: linkedDay.endTime ?? undefined,
        },
      })
    }
  }

  tripModule.fetch(trip.value.id)
  filePath.value = ''
}

async function updateActivity() {
  if (isNew) {
    return
  }

  if (filePath.value) {
    image.value = filePath.value
  }

  const updatedActivity: ActivityUpdate = {
    _id: props.activity._id,
    location: (await locationSearch.value?.fetchDetails()) ?? null,
    category: category.value ?? null,
    images: image.value ? [image.value] : [],
    summary: null,
  }

  if (summary.value !== '') {
    updatedActivity.summary = summary.value
  }

  if (name.value !== props.activity.name) {
    updatedActivity.name = name.value
  }

  updateActivityMutation.mutate(updatedActivity, {
    onSuccess: () => tripModule.fetch(trip.value.id),
  })

  filePath.value = ''
}

async function saveAndClose() {
  await saveActivity()
  emits('saved')
  closeDialog()
}

function openInMaps() {
  if (location.value) {
    openInGoogleMaps(location.value.title)
  }
}

function openDatePicker(linkedDay?: LinkedDay) {
  selectedLinkedDay.value = linkedDay
  showDatePicker.value = true
}

function linkItemToDay(newLinkedDay: NewLinkedDay) {
  if (props.activity._id) {
    linkActivityToDayMutation.mutate(
      {
        waypointId: props.waypoint._id,
        waypointDayId: newLinkedDay.dayId,
        waypointDayDate: newLinkedDay.date,
        newItem: {
          activity: props.activity as Activity,
          type: WaypointDayItemType.ACTIVITY,
          startTime: newLinkedDay.startTime ?? undefined,
          endTime: newLinkedDay.endTime ?? undefined,
        },
      },
      { onSuccess: () => tripModule.fetch(trip.value.id) },
    )
  } else {
    linkedDays.value.push({ ...newLinkedDay, itemId: '' })
  }

  showDatePicker.value = false
}

function unlinkItemFromDay(linkedDay: LinkedDay | undefined) {
  if (!linkedDay) {
    return
  }

  if (props.activity._id) {
    unlinkActivityFromDayMutation.mutate(
      {
        waypointDayId: linkedDay.dayId,
        itemId: linkedDay.itemId,
      },
      { onSuccess: () => tripModule.fetch(trip.value.id) },
    )
  } else {
    const index = linkedDays.value.indexOf(linkedDay)
    if (index >= 0) {
      linkedDays.value.splice(index, 1)
    }
  }

  showDatePicker.value = false
}

function updateTime(linkedDay: LinkedDay | undefined, time: TimeRange) {
  if (!linkedDay) {
    return
  }
  if (props.activity._id) {
    updateWaypointDayItemMutation.mutate(
      {
        waypointDayId: linkedDay.dayId,
        itemId: linkedDay.itemId,
        updateItem: time,
      },
      { onSuccess: () => tripModule.fetch(trip.value.id) },
    )
  } else {
    const foundLinkedDay = linkedDays.value.find((day) => {
      return day.dayId === linkedDay.dayId
    })

    if (foundLinkedDay) {
      foundLinkedDay.startTime = time.startTime
      foundLinkedDay.endTime = time.endTime
    }
  }

  showDatePicker.value = false
}

/**
 * Is called when a linkedDay from a allready persisted activity has changed.
 * It rearranged the WaypointDayItem to the new waypointDay.
 *
 * @param payload includes the informations about the current day and the target day.
 *
 */
function rearrangeWaypointDayItem(payload: RearrangeWaypointDayItem) {
  if (isNew) {
    return
  }

  rearrangeWaypointDayItemMutation.mutate(payload, {
    onSuccess: () => tripModule.fetch(trip.value.id),
  })

  showDatePicker.value = false
}

/**
 * Is called when a linkedDay from a new activity (not safed yet) has changed.
 */
function replaceLinkedDay(
  linkedDay: LinkedDay | undefined,
  newLinkedDay: NewLinkedDay,
) {
  if (!isNew || !linkedDay) {
    return
  }
  const foundIndex = linkedDays.value.findIndex((day) => {
    return day.dayId === linkedDay.dayId
  })
  if (foundIndex !== -1) {
    linkedDays.value[foundIndex] = { ...newLinkedDay, itemId: '' }
  }

  showDatePicker.value = false
}

function showActivityImg() {
  showLightBox.value = true
}

function showFileBrowser() {
  activityPhotoUploader.value?.click()
}

function deleteActivityImg() {
  image.value = ''
  filePath.value = ''
}

const onFileChanged = async function (e: Event): Promise<void> {
  const file = (e.target as HTMLInputElement)?.files?.[0]
  const partSize = 25 * 1024 * 1024

  if (!file) {
    return
  }

  if (file.size > partSize) {
    eventModule.newError('error.uploadFileSize')
  }

  const reader = new FileReader()
  reader.onload = (e) => {
    image.value = (e.target as any).result
  }

  reader.readAsDataURL(file)

  const fileName = `${uuidv4()}.${file.name.split('.').pop()?.toLowerCase()}`

  if (!trip.value.id) {
    return
  }

  await AWSManager.uploadFile(
    `trips/${trip.value.id}/activities/${fileName}`,
    file,
    () => {
      filePath.value = `trips/${trip.value.id}/activities/${fileName}`
      if (activityPhotoUploader.value) {
        activityPhotoUploader.value.value = ''
      }
    },
  )
}

return { __sfc: true,getEmojiFromText, getSearchUnsplashPhotos, i18n, props, emits, trip, locationSearch, showDialog, isOpen, isNew, image, category, name, previousName, location, summary, linkedDays, selectedLinkedDay, showDatePicker, showLightBox, activityImgs, activityPhotoUploader, filePath, emojis, isValidChange, createActivityMutation, updateActivityMutation, deleteActivityMutation, linkActivityToDayMutation, updateWaypointDayItemMutation, unlinkActivityFromDayMutation, rearrangeWaypointDayItemMutation, isLoading, initializeValues, onNameFieldFocusout, setCategoryEmoji, closeDialog, deleteActivity, saveActivity, createActivity, updateActivity, saveAndClose, openInMaps, openDatePicker, linkItemToDay, unlinkItemFromDay, updateTime, rearrangeWaypointDayItem, replaceLinkedDay, showActivityImg, showFileBrowser, deleteActivityImg, onFileChanged, RichTextEditor, LinkedDayItem, LinkedDayDatePickerModal, LocationSearch, LButton, DeleteModal, DropdownMenu, DropdownMenuItem, PictureViewer, supportedPhotoFileTypes, thumbnailImage }
}

})